import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {Menu, X, Search, Briefcase, LogOut, LayoutDashboard, UserCircle2, LogIn, ChevronDown, ShieldCheck, Waves, Sparkles, GraduationCap} from 'lucide-react';
import toast from "react-hot-toast";
import { useAuth } from "@/hooks/useAuth";
import MobileSearchOverlay from "@/components/MobileSearchOverlay";
import { useJobDepositFlow } from "@/hooks/useJobDepositFlow";

// ─── Wordmark logo (Archivo — modern geometric sans) ───────────────────────
function LogoMark({ className = "", dark = false }: {className?: string;dark?: boolean;}) {
  const sep = dark ? "text-white/40" : "text-gray-400";
  const tld = dark ? "text-white/50" : "text-gray-500";
  return (
    <span
      className={`inline-flex items-baseline leading-none select-none ${className}`}
      style={{
        fontFamily: "'Archivo', system-ui, sans-serif",
        fontWeight: 900,
        letterSpacing: "-0.055em"
      }}
      aria-hidden="true">

      <span className="text-red-600">BNSSA</span>
      <span className={`${sep} font-bold mx-[1px]`}>-</span>
      <span className="text-blue-600">MNS</span>
      <span className={`${tld} text-[0.5em] ml-[3px] font-bold tracking-tight relative -top-[0.05em]`}>
        .fr
      </span>
    </span>);

}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bnssaOpen, setBnssaOpen] = useState(false);
  const [mnsOpen, setMnsOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [compactMobile, setCompactMobile] = useState(false);
  const lastScrollYRef = useRef<number>(typeof window !== "undefined" ? window.scrollY : 0);
  const scrolledRef = useRef<boolean>(false);
  const compactRef = useRef<boolean>(false);
  const bnssaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mnsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  const openBnssa = () => {
    if (bnssaTimerRef.current) clearTimeout(bnssaTimerRef.current);
    setBnssaOpen(true);
  };
  const closeBnssa = () => {
    if (bnssaTimerRef.current) clearTimeout(bnssaTimerRef.current);
    bnssaTimerRef.current = setTimeout(() => setBnssaOpen(false), 120);
  };
  const openMns = () => {
    if (mnsTimerRef.current) clearTimeout(mnsTimerRef.current);
    setMnsOpen(true);
  };
  const closeMns = () => {
    if (mnsTimerRef.current) clearTimeout(mnsTimerRef.current);
    mnsTimerRef.current = setTimeout(() => setMnsOpen(false), 120);
  };

  useEffect(() => {
    return () => {
      if (bnssaTimerRef.current) clearTimeout(bnssaTimerRef.current);
      if (mnsTimerRef.current) clearTimeout(mnsTimerRef.current);
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, profile, isRecruiter, isOrganisme, isAdmin, signOut } = useAuth();
  // Flux de dépôt d'annonce recruteur (bouton « Annoncer »).
  const { openModal: openJobDeposit, modal: jobDepositModal } = useJobDepositFlow();

  useEffect(() => {
    let rafId: number | null = null;
    let accumDown = 0;
    let accumUp = 0;
    const DOWN_THRESHOLD = 56;       // px cumulés avant compactage
    const UP_THRESHOLD = 32;         // px cumulés avant restauration
    const TOP_GUARD = 140;           // zone haute : header toujours complet
    const SCROLLED_ON = 24;          // hystérésis : passe en "scrolled" au-delà
    const SCROLLED_OFF = 8;          // hystérésis : repasse en "top" en-dessous

    const update = () => {
      rafId = null;
      const y = Math.max(0, window.scrollY);

      // Hystérésis sur "scrolled" pour éviter le clignotement autour du seuil
      if (!scrolledRef.current && y > SCROLLED_ON) {
        scrolledRef.current = true;
        setScrolled(true);
      } else if (scrolledRef.current && y < SCROLLED_OFF) {
        scrolledRef.current = false;
        setScrolled(false);
      }

      const last = lastScrollYRef.current;
      const delta = y - last;
      lastScrollYRef.current = y;

      // Zone haute : toujours header complet, reset des accumulateurs
      if (y < TOP_GUARD) {
        accumDown = 0;
        accumUp = 0;
        if (compactRef.current) {
          compactRef.current = false;
          setCompactMobile(false);
        }
        return;
      }

      if (delta > 0) {
        accumDown += delta;
        accumUp = 0;
        if (accumDown >= DOWN_THRESHOLD && !compactRef.current) {
          compactRef.current = true;
          setCompactMobile(true);
        }
      } else if (delta < 0) {
        accumUp += -delta;
        accumDown = 0;
        if (accumUp >= UP_THRESHOLD && compactRef.current) {
          compactRef.current = false;
          setCompactMobile(false);
        }
      }
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
    setBnssaOpen(false);
    setMnsOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  // A11y — Mobile menu : Escape ferme + body scroll lock + restitution du focus
  useEffect(() => {
    if (!mobileOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        // Rendre le focus au bouton hamburger qui a ouvert le menu
        setTimeout(() => mobileMenuButtonRef.current?.focus(), 0);
      }
    };
    window.addEventListener("keydown", handleKey);
    // Focus initial sur le panneau pour les lecteurs d'écran
    const focusTimer = setTimeout(() => {
      mobileMenuRef.current?.focus();
    }, 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKey);
      clearTimeout(focusTimer);
    };
  }, [mobileOpen]);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href.split("?")[0]);
  };

  const isBnssaActive = location.pathname.startsWith("/bnssa");
  const isMnsActive = location.pathname.startsWith("/maitre-nageur-sauveteur");

  const handleSignOut = async () => {
    await signOut();
    setAccountOpen(false);
    setMobileOpen(false);
    toast.success("Vous êtes déconnecté.");
    navigate("/", { replace: true });
  };

  const accountSpaceHref = isOrganisme ? "/dashboard-organisme" : isRecruiter ? "/backoffice" : "/dashboard";
  const accountSpaceLabel = isOrganisme ? "Espace Organisme" : isRecruiter ? "Espace Recruteur" : "Espace Candidat";
  const displayName =
  profile?.fullName?.split(" ")[0] ||
  user?.userName?.split(" ")[0] ||
  user?.email?.split("@")[0] ||
  "Mon compte";

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled ?
      "bg-[#fafbfc]/85 backdrop-blur-xl shadow-[0_1px_0_0_rgba(0,0,0,0.04),0_4px_16px_0_rgba(0,0,0,0.05)]" :
      "bg-[#fafbfc]"}`
      }>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-[60px]" : "h-[72px]"}`}>

          {/* ── Logo ── */}
          <Link
            to="/"
            aria-label="Emplois BNSSA & MNS — Accueil"
            className="flex-shrink-0 group">

            <LogoMark className="text-[22px] sm:text-[28px] transition-transform duration-200 group-hover:scale-[1.03]" />
          </Link>

          {/* ── Desktop Nav ── */}
          <nav className="hidden lg:flex items-center gap-0.5" role="navigation" aria-label="Navigation principale">
            









            {/* BNSSA Dropdown */}
            <div
              className="relative"
              onMouseEnter={openBnssa}
              onMouseLeave={closeBnssa}>

              <Link
                to="/bnssa"
                className={`relative inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-150 ${
                isBnssaActive ? "text-red-700" : "text-gray-500 hover:text-red-700 hover:bg-red-50"}`
                }>

                <ShieldCheck className="w-3.5 h-3.5" />
                BNSSA
                <ChevronDown className={`w-3 h-3 transition-transform ${bnssaOpen ? "rotate-180" : ""}`} />
                {isBnssaActive && <span className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full bg-red-500" />}
              </Link>
              <div
                role="menu"
                onMouseEnter={openBnssa}
                onMouseLeave={closeBnssa}
                className={`absolute left-0 top-full w-64 pt-2 transition-opacity duration-150 ${
                bnssaOpen ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none"}`
                }>
                  <div className="bg-white border border-gray-100 rounded-2xl shadow-[rgba(0,0,0,0.02)_0px_0px_0px_1px,rgba(0,0,0,0.06)_0px_4px_12px,rgba(0,0,0,0.1)_0px_8px_24px] overflow-hidden">
                    <Link to="/bnssa" role="menuitem" className="flex items-center gap-2.5 px-4 py-3 text-[13px] font-bold text-red-700 hover:bg-red-50 border-b border-gray-100">
                      <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                      Tout sur le BNSSA
                    </Link>
                    <Link to="/bnssa/offres-emploi" role="menuitem" className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-gray-700 hover:bg-red-50 hover:text-red-700">
                      <Briefcase className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500" />
                      Offres d'emploi BNSSA
                    </Link>
                    <Link to="/bnssa/formations" role="menuitem" className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-gray-700 hover:bg-red-50 hover:text-red-700">
                      <GraduationCap className="w-3.5 h-3.5 text-gray-400" />
                      Formations BNSSA
                    </Link>
                    


                  </div>
                </div>
            </div>

            {/* MNS Dropdown */}
            <div
              className="relative"
              onMouseEnter={openMns}
              onMouseLeave={closeMns}>

              <Link
                to="/maitre-nageur-sauveteur"
                className={`relative inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-150 ${
                isMnsActive ? "text-blue-700" : "text-gray-500 hover:text-blue-700 hover:bg-blue-50"}`
                }>

                <Waves className="w-3.5 h-3.5" />
                MNS
                <ChevronDown className={`w-3 h-3 transition-transform ${mnsOpen ? "rotate-180" : ""}`} />
                {isMnsActive && <span className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full bg-blue-500" />}
              </Link>
              <div
                role="menu"
                onMouseEnter={openMns}
                onMouseLeave={closeMns}
                className={`absolute left-0 top-full w-64 pt-2 transition-opacity duration-150 ${
                mnsOpen ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none"}`
                }>
                  <div className="bg-white border border-gray-100 rounded-2xl shadow-[rgba(0,0,0,0.02)_0px_0px_0px_1px,rgba(0,0,0,0.06)_0px_4px_12px,rgba(0,0,0,0.1)_0px_8px_24px] overflow-hidden">
                    <Link to="/maitre-nageur-sauveteur" role="menuitem" className="flex items-center gap-2.5 px-4 py-3 text-[13px] font-bold text-blue-700 hover:bg-blue-50 border-b border-gray-100">
                      <Waves className="w-4 h-4 flex-shrink-0" />
                      Tout sur le MNS
                    </Link>
                    <Link to="/maitre-nageur-sauveteur/offres-emploi" role="menuitem" className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-700">
                      <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                      Offres d'emploi MNS
                    </Link>
                    <Link to="/maitre-nageur-sauveteur/formations" role="menuitem" className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-700">
                      <GraduationCap className="w-3.5 h-3.5 text-gray-400" />
                      Formations MNS
                    </Link>
                    


                  </div>
                </div>
            </div>

            {/* Disponibles Immédiatement (CVthèque) */}
            <Link
              to="/disponibles"
              className={`relative inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-150 ${
              isActive("/disponibles") ? "text-amber-700" : "text-gray-500 hover:text-amber-700 hover:bg-amber-50"}`
              }>
              <Sparkles className="w-3.5 h-3.5" />
              Ils sont disponibles&nbsp;!
              {isActive("/disponibles") && <span className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full bg-amber-500" />}
            </Link>

            {/* Other nav links */}
            {[
            { label: "Formations", href: "/formations" }].
            map((link) =>
            <Link
              key={link.href}
              to={link.href}
              className={`relative px-3.5 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-150 ${
              isActive(link.href) ? "text-gray-900" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"}`
              }>

                {link.label}
                {isActive(link.href) && <span className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full bg-gray-900" />}
              </Link>
            )}
          </nav>

          {/* ── Desktop Actions ── */}
          <div className="hidden lg:flex items-center gap-2">
            <Link
              to="/offre-emploi"
              className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">

              <Search className="w-3.5 h-3.5" />
              Rechercher
            </Link>

            {isAuthenticated ?
            <div className="relative">
                <button
                type="button"
                onClick={() => setAccountOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                className="flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-xl text-[13px] font-medium text-gray-700 hover:bg-gray-50 border border-gray-200 transition-colors">

                  <span className="w-7 h-7 rounded-full bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
                    {displayName[0]?.toUpperCase()}
                  </span>
                  <span className="max-w-[120px] truncate">{displayName}</span>
                  <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${accountOpen ? "rotate-180" : ""}`} />
                </button>
                {accountOpen &&
              <div
                role="menu"
                className="absolute right-0 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-[rgba(0,0,0,0.02)_0px_0px_0px_1px,rgba(0,0,0,0.06)_0px_4px_12px,rgba(0,0,0,0.1)_0px_8px_24px] overflow-hidden"
                onMouseLeave={() => setAccountOpen(false)}>

                    <div className="px-4 py-3.5 bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
                      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Connecté</p>
                      <p className="text-sm font-semibold text-[#222222] truncate mt-0.5">{user?.email}</p>
                      <p className="text-[11px] mt-0.5 text-gray-500">{isAdmin ? "Admin — choix d'espace" : accountSpaceLabel}</p>
                    </div>
                    {isAdmin ?
                <div className="py-1">
                      <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        Choisir mon espace
                      </p>
                      <Link to="/dashboard" onClick={() => setAccountOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" role="menuitem">
                        <UserCircle2 className="w-4 h-4 text-red-500" />
                        Espace Candidat
                      </Link>
                      <Link to="/backoffice" onClick={() => setAccountOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" role="menuitem">
                        <Briefcase className="w-4 h-4 text-gray-700" />
                        Espace Recruteur
                      </Link>
                      <Link to="/dashboard-organisme" onClick={() => setAccountOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" role="menuitem">
                        <GraduationCap className="w-4 h-4 text-amber-600" />
                        Espace Organisme
                      </Link>
                    </div> :

                <Link
                  to={accountSpaceHref}
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  role="menuitem">

                      <LayoutDashboard className="w-4 h-4 text-gray-400" />
                      {accountSpaceLabel}
                    </Link>
                }
                    {isAdmin &&
                <Link
                  to="/admin"
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-3 text-sm text-amber-700 bg-amber-50/40 hover:bg-amber-50 transition-colors border-t border-gray-100"
                  role="menuitem">

                        <ShieldCheck className="w-4 h-4 text-amber-600" />
                        Admin — Validations
                      </Link>
                }
                    <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-red-500 hover:bg-red-50 border-t border-gray-100 transition-colors"
                  role="menuitem">

                      <LogOut className="w-4 h-4" />
                      Se déconnecter
                    </button>
                  </div>
              }
              </div> :

            <>
                <Link
                to="/connexion"
                className="flex items-center gap-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">

                  <LogIn className="w-3.5 h-3.5" />
                  Connexion
                </Link>
                <button
                type="button"
                onClick={openJobDeposit}
                className="flex items-center gap-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">

                  <Briefcase className="w-3.5 h-3.5" />
                  Annoncer
                </button>
                <Link
                to="/inscription"
                className="bg-[#222222] text-white px-4 py-2 rounded-xl text-[13px] font-semibold hover:bg-gray-800 transition-colors">

                  Postuler
                </Link>
              </>
            }
          </div>

          {/* ── Mobile actions: search + hamburger labellisé ── */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              type="button"
              className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
              onClick={() => setMobileSearchOpen(true)}
              aria-label="Ouvrir la recherche d'offres d'emploi"
              aria-haspopup="dialog">

              <Search className="w-5 h-5" aria-hidden="true" focusable="false" />
            </button>
            <button
              ref={mobileMenuButtonRef}
              type="button"
              className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-2 rounded-xl text-gray-700 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Fermer le menu de navigation" : "Ouvrir le menu de navigation"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-panel"
              aria-haspopup="menu">

              {mobileOpen ?
              <X className="w-5 h-5" aria-hidden="true" focusable="false" /> :
              <Menu className="w-5 h-5" aria-hidden="true" focusable="false" />
              }
              <span className="text-[12.5px] font-semibold leading-none" aria-hidden="true">
                {mobileOpen ? "Fermer" : "Menu"}
              </span>
            </button>
          </div>
        </div>

        {/* ── Mobile combo nav: BNSSA / MNS / Disponibles chips (auto-hide fluide on scroll-down) ── */}
        <div
          className="lg:hidden overflow-hidden"
          style={{
            height: compactMobile && !mobileOpen ? 0 : 54,
            transition: "height 280ms cubic-bezier(0.22, 1, 0.36, 1)"
          }}
          aria-hidden={compactMobile && !mobileOpen}>
          <nav
            className="flex items-center gap-2 overflow-x-auto no-scrollbar pt-1.5 pb-2.5 transform-gpu"
            style={{
              opacity: compactMobile && !mobileOpen ? 0 : 1,
              transform: compactMobile && !mobileOpen ? "translate3d(0,-8px,0)" : "translate3d(0,0,0)",
              transition: "opacity 220ms cubic-bezier(0.22, 1, 0.36, 1), transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              willChange: "transform, opacity",
              pointerEvents: compactMobile && !mobileOpen ? "none" : "auto"
            }}
            aria-label="Navigation rapide par univers">
          <Link
            to="/bnssa"
            aria-current={isBnssaActive ? "page" : undefined}
            tabIndex={compactMobile && !mobileOpen ? -1 : 0}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold border whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
            isBnssaActive ?
            "bg-red-600 text-white border-red-600" :
            "bg-white text-red-700 border-red-200 hover:bg-red-50"}`
            }>

            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" focusable="false" />
            BNSSA
          </Link>
          <Link
            to="/maitre-nageur-sauveteur"
            aria-current={isMnsActive ? "page" : undefined}
            tabIndex={compactMobile && !mobileOpen ? -1 : 0}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold border whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
            isMnsActive ?
            "bg-blue-600 text-white border-blue-600" :
            "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"}`
            }>

            <Waves className="w-3.5 h-3.5" aria-hidden="true" focusable="false" />
            MNS
          </Link>
          <Link
            to="/disponibles"
            aria-current={isActive("/disponibles") ? "page" : undefined}
            tabIndex={compactMobile && !mobileOpen ? -1 : 0}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold border whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
            isActive("/disponibles") ?
            "bg-amber-500 text-white border-amber-500" :
            "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"}`
            }>

            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" focusable="false" />
            Disponibles
          </Link>
          </nav>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      {mobileOpen &&
      <div
        ref={mobileMenuRef}
        id="mobile-menu-panel"
        role="navigation"
        aria-label="Menu principal"
        tabIndex={-1}
        className="lg:hidden border-t border-gray-100 bg-white/98 backdrop-blur-md shadow-xl focus:outline-none">
          <div className="px-4 py-4 space-y-1">
            









            {/* BNSSA mobile */}
            <div className="px-3.5 py-2">
              <Link
              to="/bnssa"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-red-700">

                <ShieldCheck className="w-3.5 h-3.5" />
                BNSSA
              </Link>
              <div className="mt-2 ml-2 space-y-1 border-l-2 border-red-100 pl-3">
                <Link to="/bnssa/offres-emploi" onClick={() => setMobileOpen(false)} className="block py-1.5 text-sm text-gray-700 hover:text-red-700">Offres d'emploi</Link>
                <Link to="/bnssa/formations" onClick={() => setMobileOpen(false)} className="block py-1.5 text-sm text-gray-700 hover:text-red-700">Formations</Link>
                
              </div>
            </div>

            {/* MNS mobile */}
            <div className="px-3.5 py-2">
              <Link
              to="/maitre-nageur-sauveteur"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-700">

                <Waves className="w-3.5 h-3.5" />
                MNS
              </Link>
              <div className="mt-2 ml-2 space-y-1 border-l-2 border-blue-100 pl-3">
                <Link to="/maitre-nageur-sauveteur/offres-emploi" onClick={() => setMobileOpen(false)} className="block py-1.5 text-sm text-gray-700 hover:text-blue-700">Offres d'emploi</Link>
                <Link to="/maitre-nageur-sauveteur/formations" onClick={() => setMobileOpen(false)} className="block py-1.5 text-sm text-gray-700 hover:text-blue-700">Formations</Link>
                
              </div>
            </div>

            <Link
              to="/disponibles"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3.5 py-3 rounded-xl text-sm font-semibold text-amber-700 bg-amber-50">
              <Sparkles className="w-4 h-4" />
              Ils sont disponibles&nbsp;!
            </Link>

            {[
          { label: "Formations", href: "/formations" }].
          map((link) =>
          <Link
            key={link.href}
            to={link.href}
            onClick={() => setMobileOpen(false)}
            className={`flex items-center px-3.5 py-3 rounded-xl text-sm font-medium transition-colors ${
            isActive(link.href) ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`
            }>

                {isActive(link.href) && <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2.5 flex-shrink-0" />}
                {link.label}
              </Link>
          )}

            {isAdmin &&
          <Link
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3.5 py-3 rounded-xl text-sm font-semibold text-amber-700 bg-amber-50">

                <ShieldCheck className="w-4 h-4" />
                Admin — Validations
              </Link>
          }

            <div className="pt-3 border-t border-gray-100 space-y-2 mt-2">
              {isAuthenticated ?
            <>
                  <div className="px-3.5 py-3 bg-gray-50 rounded-xl flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {displayName[0]?.toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Connecté</p>
                      <p className="text-sm font-semibold text-gray-900 truncate">{user?.email}</p>
                    </div>
                  </div>
                  {isAdmin ?
                <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-1">Choisir mon espace</p>
                      <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 w-full bg-[#222222] text-white px-4 py-3 rounded-xl text-sm font-semibold">
                        <UserCircle2 className="w-4 h-4" />
                        Espace Candidat
                      </Link>
                      <Link to="/backoffice" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 w-full border border-gray-300 text-gray-800 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50">
                        <Briefcase className="w-4 h-4" />
                        Espace Recruteur
                      </Link>
                      <Link to="/dashboard-organisme" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 w-full border border-gray-300 text-gray-800 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50">
                        <GraduationCap className="w-4 h-4" />
                        Espace Organisme
                      </Link>
                    </div> :

                <Link
                to={accountSpaceHref}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full bg-[#222222] text-white px-4 py-3 rounded-xl text-sm font-semibold">

                    <LayoutDashboard className="w-4 h-4" />
                    {accountSpaceLabel}
                  </Link>
                }
                  <button
                type="button"
                onClick={handleSignOut}
                className="flex items-center justify-center gap-2 w-full bg-white border border-red-200 text-red-500 px-4 py-3 rounded-xl text-sm font-semibold">

                    <LogOut className="w-4 h-4" />
                    Se déconnecter
                  </button>
                </> :

            <>
                  <Link
                to="/connexion"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full border border-gray-200 text-gray-700 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50">

                    <LogIn className="w-4 h-4" />
                    Connexion
                  </Link>
                  <Link
                to="/inscription"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full bg-[#222222] text-white px-4 py-3 rounded-xl text-sm font-semibold">

                    <UserCircle2 className="w-4 h-4" />
                    Postuler — Espace Candidat
                  </Link>
                  <button
                type="button"
                onClick={() => { setMobileOpen(false); openJobDeposit(); }}
                className="flex items-center justify-center gap-2 w-full border border-gray-200 text-gray-700 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50">

                    <Briefcase className="w-4 h-4" />
                    Annoncer
                  </button>
                </>
            }
            </div>
          </div>
        </div>
      }

      {/* Fine ligne dégradée rouge → bleu (signature BNSSA / MNS) */}
      <div
        aria-hidden="true"
        className="h-[2px] w-full bg-gradient-to-r from-red-500 via-fuchsia-400 to-blue-500 opacity-80" />

      {/* Mobile full-screen search overlay */}
      <MobileSearchOverlay open={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)} />

      {/* Modal de dépôt d'annonce ouvert depuis le bouton « Annoncer ». */}
      {jobDepositModal}
    </header>);

}